<script setup>
import ModalEmailEdit from '@/components/edit/ModalEmailEdit.vue'
import ModalMain from '@/components/modal/ModalMain.vue'
import PreferenceBox from '@/components/preference/PreferenceBox.vue'
import PreferenceEditEmail from '@/components/preference/elements/PreferenceEditEmail.vue'
import PreferenceList from '@/components/preference/PreferenceList.vue'
import PreferenceLogo from '@/components/preference/PreferenceLogo.vue'
import AutoSaveStatus from '@/components/preference/elements/AutoSaveStatus.vue'
import EmailprefBadge from '@/components/EmailprefBadge.vue'

import { withProps, usePreferenceTemplate } from '@/components/preference/templates/preferenceTemplate.js'

const props = defineProps({
  ...withProps(),
  listComponent: {
    type: Object,
    default: () => PreferenceList,
  },
  listAttributes: {
    type: Object,
    default: null,
  },
  outerClass: {
    type: String,
    default: '',
  },
  outerStyle: {
    type: Object,
    default: null,
  },
  containerClass: {
    type: String,
    default: 'min-h-screen max-h-[3840px] flex flex-col items-center justify-center',
  },
  cardComponent: {
    type: [Object, String],
    default: () => PreferenceBox,
  },
  cardClass: {
    type: String,
    default: '',
  },
  contentClass: {
    type: String,
    default: 'my-6',
  },
  mainClass: {
    type: String,
    default: 'my-6',
  },
})

const emit = defineEmits(['save', 'userEmail', 'update'])
const { t, alertMsg, chosenEmail, newEmail, modal, modalType, setEmail, editEmail, updateEmail, updateGlobal, savePreferences } = usePreferenceTemplate(
  props,
  emit,
)

function updateChosenEmail(val) {
  chosenEmail.value = val
}

function closeModal() {
  modal.value = false
}

const slotScope = {
  get formStep() {
    return { logo: props.logo, attrs: props.attrs, name: props.name, chosenEmail: chosenEmail.value, updateChosenEmail, alertMsg: alertMsg.value, setEmail, t }
  },
  get preferencesHeader() {
    return { logo: props.logo, attrs: props.attrs, name: props.name, t }
  },
  get saveFeedback() {
    return { saved: props.saved, saveStatus: props.saveStatus, attrs: props.attrs, alertMsg: alertMsg.value, t }
  },
  get actionButtons() {
    return { attrs: props.attrs, disabled: props.disabled, updateGlobal, savePreferences, saved: props.saved, t, emit }
  },
  get modalSlot() {
    return {
      modal: modal.value,
      modalType: modalType.value,
      chosenEmail: chosenEmail.value,
      customerId: props.customerId,
      attrs: props.attrs,
      preview: props.preview,
      updateEmail,
      closeModal,
    }
  },
  get badge() {
    return { attrs: props.attrs }
  },
}
</script>

<template>
  <div class="min-h-screen max-h-fit w-full" :class="outerClass" :style="outerStyle || { backgroundColor: props.attrs?.background_color || '#F8F8F8' }">
    <slot name="background" />

    <div :class="containerClass">
      <component :is="cardComponent" :class="cardClass">
        <slot name="card-prepend" />

        <!-- Form step -->
        <div v-if="currentStep == 'form'" :class="contentClass">
          <slot name="form-step" v-bind="slotScope.formStep">
            <PreferenceLogo :url="logo" />
            <div class="mx-6 text-center">
              <h1 class="text-2xl font-medium mt-8">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h1>
              <h2 class="text-gray-400">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</h2>
            </div>
            <form class="flex flex-col text-center items-center mt-5 space-y-4" @submit="setEmail($event, chosenEmail)">
              <div class="w-full px-6">
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ $t('pref_page_email_form.form_title') }}</label>
                <input
                  id="email"
                  v-model="chosenEmail"
                  type="email"
                  name="email"
                  class="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  :placeholder="$t('pref_page_email_form.form_placeholder').replace('[at]', '@')"
                />
                <span v-if="alertMsg" class="text-red-500 text-xs mt-1 block">{{ alertMsg }}</span>
              </div>
              <button
                type="submit"
                class="rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 w-[90%]"
                :style="{ backgroundColor: props.attrs?.primary_color || '#0099d0' }"
              >
                {{ $t('pref_page_email_form.form_save_btn') }}
              </button>
            </form>
          </slot>
        </div>

        <!-- Preferences step -->
        <div v-else :class="contentClass">
          <header>
            <slot name="preferences-header" v-bind="slotScope.preferencesHeader">
              <PreferenceLogo :url="logo" />
              <div class="text-center">
                <h2 class="text-3xl mt-4">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h2>
                <p class="text-lg my-2 text-gray-500">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>
              </div>
            </slot>
          </header>

          <main :class="mainClass">
            <PreferenceEditEmail v-if="attrs.allow_email_change" :new-email="newEmail" :chosen-email="chosenEmail" :disabled="disabled" @edit="editEmail" />
            <component :is="listComponent" :disabled="disabled" :lists="lists" :attributes="listAttributes || attrs" @update="$emit('update')" />
          </main>

          <slot name="save-feedback" v-bind="slotScope.saveFeedback">
            <AutoSaveStatus v-if="attrs?.auto_save" :status="saveStatus" :color="attrs?.primary_color" />
            <Transition v-else name="slide-fade">
              <div v-if="saved" class="saved mx-2 my-4 p-2 text-center bg-green-100 text-green-900 rounded-md">
                {{ attrs?.saved_preferences_msg || $t('pref_page.save_msg') || 'Your preferences have been saved' }}
              </div>
            </Transition>
          </slot>

          <div v-if="attrs.unsubscribe_all || !attrs?.auto_save">
            <slot name="action-buttons" v-bind="slotScope.actionButtons">
              <div class="mt-8 flex w-full justify-center space-x-5">
                <button
                  v-if="attrs.unsubscribe_all"
                  data-test="unsubscribe-all-button"
                  class="rounded-md px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-white hover:bg-gray-50"
                  @click="!disabled ? updateGlobal() : null"
                >
                  {{ $t('pref_page.unsubscribe_btn') }}
                </button>
                <button
                  v-if="!attrs?.auto_save"
                  class="rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                  :style="{ backgroundColor: props.attrs?.primary_color || '#0099d0' }"
                  @click="$emit('save')"
                >
                  {{ $t('pref_page.save_btn') }}
                </button>
              </div>
            </slot>
          </div>
        </div>

        <slot name="modal" v-bind="slotScope.modalSlot">
          <ModalMain v-if="modal" @close="modal = false">
            <ModalEmailEdit
              v-if="modalType === 'email'"
              :email="chosenEmail"
              :customer-id="customerId"
              :attributes="attrs"
              :preview="preview"
              @save="updateEmail"
              @close="modal = false"
            />
          </ModalMain>
        </slot>
      </component>

      <slot name="badge" v-bind="slotScope.badge">
        <EmailprefBadge v-if="!props.attrs?.remove_badge" />
      </slot>
    </div>
  </div>
</template>

<style>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
